import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/auth/RoleGuard';
import { AppShell } from '@/components/layout/AppShell';

// Public Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { ShopCatalogPage } from '@/pages/public/ShopCatalogPage';
import { ShopDetailPage } from '@/pages/public/ShopDetailPage';
import { PublicInvoicePage } from '@/pages/public/PublicInvoicePage';

// App Pages
import { DashboardPage } from '@/pages/app/DashboardPage';
import { LaptopsPage } from '@/pages/app/LaptopsPage';
import { AccessoriesPage } from '@/pages/app/AccessoriesPage';
import { PurchasesPage } from '@/pages/app/PurchasesPage';
import { SalesPage } from '@/pages/app/SalesPage';
import { ReturnsPage } from '@/pages/app/ReturnsPage';
import { CustomersPage } from '@/pages/app/CustomersPage';
import { SuppliersPage } from '@/pages/app/SuppliersPage';
import { RepairsPage } from '@/pages/app/RepairsPage';
import { WarrantiesPage } from '@/pages/app/WarrantiesPage';
import { ExpensesPage } from '@/pages/app/ExpensesPage';
import { ReportsPage } from '@/pages/app/ReportsPage';
import { AuditPage } from '@/pages/app/AuditPage';
import { SettingsPage } from '@/pages/app/SettingsPage';
import { ScannerPage } from '@/pages/app/ScannerPage';
import { StocktakePage } from '@/pages/app/StocktakePage';
import { CatalogManagementPage } from '@/pages/app/CatalogManagementPage';

export const router = createBrowserRouter([
  // Public Routes
  {
    path: '/',
    element: <Navigate to="/app" replace />,
  },
  {
    path: '/shop',
    element: <ShopCatalogPage />,
  },
  {
    path: '/shop/:slug',
    element: <ShopDetailPage />,
  },
  {
    path: '/shop/:type/:id',
    element: <ShopDetailPage />,
  },
  {
    path: '/invoice/share/:token',
    element: <PublicInvoicePage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },

  // Authenticated Application Shell Routes
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'inventory/laptops',
        element: <LaptopsPage />,
      },
      {
        path: 'inventory/accessories',
        element: <AccessoriesPage />,
      },
      {
        path: 'catalog',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <CatalogManagementPage />
          </RoleGuard>
        ),
      },
      {
        path: 'purchases',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <PurchasesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'sales',
        element: <SalesPage />,
      },
      {
        path: 'returns',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <ReturnsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'customers',
        element: <CustomersPage />,
      },
      {
        path: 'suppliers',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <SuppliersPage />
          </RoleGuard>
        ),
      },
      {
        path: 'repairs',
        element: <RepairsPage />,
      },
      {
        path: 'warranties',
        element: <WarrantiesPage />,
      },
      {
        path: 'expenses',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <ExpensesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <ReportsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'audit',
        element: (
          <RoleGuard allowedRoles={['owner']}>
            <AuditPage />
          </RoleGuard>
        ),
      },
      {
        path: 'settings',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <SettingsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'scanner',
        element: <ScannerPage />,
      },
      {
        path: 'stocktake',
        element: (
          <RoleGuard allowedRoles={['owner', 'manager']}>
            <StocktakePage />
          </RoleGuard>
        ),
      },
    ],
  },

  // Fallback redirect
  {
    path: '*',
    element: <Navigate to="/app" replace />,
  },
]);
